import {
    IonAlert,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardSubtitle,
    IonCardTitle,
    IonContent,
    IonItem,
    IonLabel,
    IonPage,
    IonSkeletonText,
    useIonToast,
    useIonRouter,
    useIonViewWillEnter,
    IonButton,
    IonIcon,
} from '@ionic/react';
import { createOutline, documentTextOutline, trashOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';

const ViewDocument: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const documentName = location.state as string;
    const router = useIonRouter();
    const history = useHistory();
    const [present] = useIonToast();

    const [document, setDocument] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);

    const getStatusColor = (status: string) => {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'compliant' || normalized === 'completed') return '#00a65a';
        if (normalized.includes('non compliant') || normalized.includes('need renewal') || normalized.includes('need inspection')) return '#f56954';
        return '#f39c12';
    };

    const getExpiryChip = (expiryDate: string | null | undefined): { label: string; bg: string; color: string } | null => {
        if (!expiryDate) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiry = new Date(expiryDate);
        expiry.setHours(0, 0, 0, 0);
        const daysLeft = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (daysLeft < 0) return { label: t('Documents.Document_Expired'), bg: '#fdecea', color: '#c0392b' };
        if (daysLeft <= 30) return { label: t('Documents.Due_Renewal_in_30_Days'), bg: '#fff3e0', color: '#e65100' };
        if (daysLeft <= 90) return { label: t('Documents.About_to_Expire_in_90_Days'), bg: '#fffde7', color: '#f57f17' };
        return null;
    };

    const getDocument = async () => {
        if (!documentName) return;

        const config: any = {
            method: 'get',
            url: `/resource/Compliance Documents/${documentName}`,
            headers: {
                Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
            },
        };

        setIsLoading(true);
        try {
            const response = await axios(config);
            if (response?.data?.data) {
                setDocument(response.data.data);
            }
        } catch (error: any) {
            console.log(error);
            present({
                message: t('Utils.fetchF'),
                color: 'danger',
                duration: 2000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useIonViewWillEnter(() => {
        getDocument();
    });

    const openAttachment = (attachmentUrl: string) => {
        if (!attachmentUrl) return;
        const baseUrl = localStorage.getItem('baseUrl')?.replace('/api', '') || '';
        const fullUrl = `${baseUrl}${attachmentUrl}`;
        window.open(fullUrl, '_system');
    };

    const deleteDocument = async () => {
        try {
            await axios({
                method: 'delete',
                url: `/resource/Compliance%20Documents/${documentName}`,
                headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
            });
            present({ message: t('Documents.documentDeleted', 'Document deleted'), duration: 2000, color: 'success' });
            history.push('/documents');
        } catch (e) {
            console.log('Delete error', e);
            present({ message: t('Documents.documentDeleteFailed', 'Failed to delete document'), duration: 2500, color: 'danger' });
        }
    };

    if (isLoading) {
        return (
            <IonPage>
                <Header enableBackButton="/documents" title={t('Documents.list')} />
                <IonContent>
                    <div style={{ padding: '1rem' }}>
                        <IonSkeletonText animated style={{ width: '60%', height: '24px', marginBottom: '1rem' }} />
                        <IonSkeletonText animated style={{ width: '100%', height: '20px', marginBottom: '0.5rem' }} />
                        <IonSkeletonText animated style={{ width: '80%', height: '20px', marginBottom: '0.5rem' }} />
                        <IonSkeletonText animated style={{ width: '90%', height: '20px', marginBottom: '1rem' }} />
                        <IonSkeletonText animated style={{ width: '100%', height: '200px' }} />
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    if (!document) {
        return (
            <IonPage>
                <Header enableBackButton="/documents" title={t('Documents.list')} />
                <IonContent>
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <p>{t('Documents.documentNotFound', 'Document not found.')}</p>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const assignedProjects = document.assigned_projects?.map((u: any) => u.project)?.join(', ');
    const assignedAssets = document.assigned_assets?.map((u: any) => u.asset)?.join(', ');
    const assignedUsers = document.assigned_users?.map((u: any) => u?.name || u.user)?.join(', ');

    const isImage = document?.attachment?.match(/\.(jpeg|jpg|gif|png)$/) != null;

    return (
        <IonPage>
            <Header enableBackButton="/documents" title={t('Documents.list')} />
            <IonContent>
                {/* Delete confirmation */}
                <IonAlert
                    isOpen={showDeleteAlert}
                    onDidDismiss={() => setShowDeleteAlert(false)}
                    header={t('Documents.deleteAlertTitle')}
                    message={t('Documents.deleteAlertMessage', { name: document?.document_name })}
                    buttons={[
                        { text: t('Documents.cancel'), role: 'cancel' },
                        { text: t('Documents.delete'), role: 'destructive', handler: deleteDocument },
                    ]}
                />
                <div style={{ paddingTop: '1rem', paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '3rem' }}>
                    <IonCard style={{ margin: 0, width: '100%' }}>
                        <IonCardHeader>
                            <IonCardTitle>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>{document.document_name}</span>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonButton
                                            fill="clear"
                                            size="small"
                                            onClick={() => history.push('/documents/edit', documentName)}
                                            style={{ '--color': '#1d508d' }}
                                        >
                                            <IonIcon icon={createOutline} slot="icon-only" />
                                        </IonButton>
                                        <IonButton
                                            fill="clear"
                                            size="small"
                                            onClick={() => setShowDeleteAlert(true)}
                                            style={{ '--color': '#c0392b' }}
                                        >
                                            <IonIcon icon={trashOutline} slot="icon-only" />
                                        </IonButton>
                                    </div>
                                </div>
                            </IonCardTitle>
                            <IonCardSubtitle style={{ marginTop: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div
                                        style={{
                                            marginRight: '8px',
                                            height: '10px',
                                            width: '10px',
                                            backgroundColor: getStatusColor(document.status),
                                            borderRadius: '50%',
                                        }}
                                    ></div>
                                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{document.status}</span>
                                </div>
                                {(() => {
                                    const chip = getExpiryChip(document.expiry_date);
                                    if (!chip) return null;
                                    return (
                                        <span style={{
                                            display: 'inline-block',
                                            marginTop: '8px',
                                            padding: '3px 10px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            background: chip.bg,
                                            color: chip.color,
                                            border: `1px solid ${chip.color}33`,
                                        }}>
                                            {chip.label}
                                        </span>
                                    );
                                })()}
                            </IonCardSubtitle>
                        </IonCardHeader>

                        <IonCardContent>
                            <div className="data-list">
                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.document_number')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{document.document_number || '-'}</h3>
                                    </IonLabel>
                                </IonItem>

                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.document_category')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{document.document_category || '-'}</h3>
                                    </IonLabel>
                                </IonItem>

                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.document_type')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{document.document_type || '-'}</h3>
                                    </IonLabel>
                                </IonItem>

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <IonItem lines="none" style={{ '--padding-start': '0', flex: 1 }}>
                                        <IonLabel className="ion-text-wrap">
                                            <p style={{ fontWeight: 'bold' }}>{t('Documents.issuedDate')}</p>
                                            <h3 style={{ marginTop: '4px', color: '#333' }}>{document.issued_date || '-'}</h3>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem lines="none" style={{ '--padding-start': '0', flex: 1 }}>
                                        <IonLabel className="ion-text-wrap">
                                            <p style={{ fontWeight: 'bold' }}>{t('Documents.expiryDate')}</p>
                                            <h3 style={{ marginTop: '4px', color: '#333' }}>{document.expiry_date || '-'}</h3>
                                        </IonLabel>
                                    </IonItem>
                                </div>

                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.issuing_entity')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{document.issuing_entity || '-'}</h3>
                                    </IonLabel>
                                </IonItem>

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <IonItem lines="none" style={{ '--padding-start': '0', flex: 1 }}>
                                        <IonLabel className="ion-text-wrap">
                                            <p style={{ fontWeight: 'bold' }}>{t('Documents.days_to_renewal')}</p>
                                            <h3 style={{ marginTop: '4px', color: '#333' }}>{document.days_to_renewal || '-'}</h3>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem lines="none" style={{ '--padding-start': '0', flex: 1 }}>
                                        <IonLabel className="ion-text-wrap">
                                            <p style={{ fontWeight: 'bold' }}>{t('Documents.reminder_days')}</p>
                                            <h3 style={{ marginTop: '4px', color: '#333' }}>{document.reminder_days || '-'}</h3>
                                        </IonLabel>
                                    </IonItem>
                                </div>

                                <div style={{ marginTop: '1rem', marginBottom: '1rem', borderTop: '1px solid #eee' }}></div>

                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.assigned_projects_input')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{assignedProjects || '-'}</h3>
                                    </IonLabel>
                                </IonItem>

                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.assigned_assets_input')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{assignedAssets || '-'}</h3>
                                    </IonLabel>
                                </IonItem>

                                <IonItem lines="none" style={{ '--padding-start': '0' }}>
                                    <IonLabel className="ion-text-wrap">
                                        <p style={{ fontWeight: 'bold' }}>{t('Documents.assigned_users_input')}</p>
                                        <h3 style={{ marginTop: '4px', color: '#333' }}>{assignedUsers || '-'}</h3>
                                    </IonLabel>
                                </IonItem>
                            </div>
                        </IonCardContent>
                    </IonCard>

                    {document.attachment && (
                        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1rem' }}>{t('Documents.attachment')}</h3>

                            {isImage ? (
                                <div
                                    onClick={() => openAttachment(document.attachment)}
                                    style={{
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        display: 'inline-block'
                                    }}
                                >
                                    <img
                                        src={`${localStorage.getItem('baseUrl')?.replace('/api', '') || ''}${document.attachment}`}
                                        alt="Document Attachment"
                                        style={{ maxWidth: '100%', maxHeight: '400px', display: 'block' }}
                                    />
                                    <div style={{ padding: '8px', background: '#f4f5f8', fontSize: '12px', color: '#666' }}>
                                        {t('Documents.viewFullImage')}
                                    </div>
                                </div>
                            ) : (
                                <IonButton
                                    expand="block"
                                    onClick={() => openAttachment(document.attachment)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon slot="start" icon={documentTextOutline} />
                                    {t('Documents.viewFileAttachment')}
                                </IonButton>
                            )}
                        </div>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default ViewDocument;
